import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';

const Signup: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setError('');
      setLoading(true);

      const { data, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name }
        }
      });

      if (authError) {
        console.error('Signup auth error:', authError);
        setError(authError.message);
        setLoading(false);
        return;
      }

      if (!data.user) {
        console.error('No user data returned from signup');
        setError('Failed to create account - no user data');
        setLoading(false);
        return;
      }

      // Create user record with auth.users.id as primary key
      const userRecord = {
        id: data.user.id,
        name: name,
        email: email,
        role: 'owner',
      };

      const { data: insertResult, error: userError } = await supabase
        .from('users')
        .upsert([userRecord], { onConflict: 'id' })
        .select();

      if (userError) {
        console.error('User record creation error:', userError);
        setError(`Account created but user record setup failed: ${userError.message}`);
        setLoading(false);
        return;
      }

      // Success - redirect to create business profile
      setTimeout(() => {
        navigate('/create-business', { replace: true });
      }, 1000);

    } catch (err) {
      console.error('Signup: Exception:', err);
      setError('Failed to create account');
      setLoading(false);
    }
  };

  return (
    <div className="bg-white py-8 px-4 shadow rounded-lg sm:px-10">
      <form className="space-y-6" onSubmit={handleSubmit}>
        <h2 className="text-2xl font-bold text-center text-gray-900">Create your account</h2>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}
        
        <Input
          label="Full Name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        
        <Input
          label="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        
        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        
        <Input
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
        
        <Button
          type="submit"
          variant="primary"
          fullWidth
          disabled={loading}
        >
          {loading ? 'Creating account...' : 'Create account'}
        </Button>

      </form>
      
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;